import { useContext, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import Ajv from 'ajv';

import env from 'configures';

import {
  LP_URL,
  STATION_URL,
  TYPE_URL,
  SKILL_URL,
  COND_URL,
  INFO_URL,
  CONF_URL,
  THANKS_URL,
  PAGE_MAP,
  CHOICES_NAME_MAP,
  ERROR_MESSAGES,
  NO_NAME_ERROR_MESSAGES,
  CUSTOM_ERROR_MESSAGES,
  DROPDOWN_INITIAL_ITEM,
  CHOICES_MAP,
  FATAL_ERROR,
  MOVE_CONF_MSG,
} from 'utils/constant';
import Measure from 'utils/measure';
import { checkEkiCodeApi } from 'utils/api';

import AppContext from 'fluxes/context';
import { UPDATE_STATION, CHANGE_VALID } from 'fluxes/actions';

export const scrollTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
};

export const isFatalApiError = (e) => {
  if(e.message === FATAL_ERROR) return true;
  if(e.message === 'Network Error') return true;
  if(e.response){
    if(e.response.status === 500) return true;
    if(e.response.status === 503) return true;
  }
  console.log('isFatalApiError: safe: ', { e });
  return false;
};

// date型を文字列に変換
export const changeStr = (date) => {
  if(!date) return '';

  const yearStr = date.getFullYear();
  let monthStr = 1 + date.getMonth();
  let dayStr = date.getDate();
    
  monthStr = (`0${ monthStr}`).slice(-2);
  dayStr = (`0${ dayStr}`).slice(-2);
    
  let formatStr = 'YYYY/MM/DD';
  formatStr = formatStr.replace(/YYYY/g, yearStr);
  formatStr = formatStr.replace(/MM/g, monthStr);
  formatStr = formatStr.replace(/DD/g, dayStr);
  return formatStr;
};

// 選択肢名と値を渡すと、ラベルを返却
export const getChoiceLabel = (choiceName, value) => {
  if(!value) return '';

  const choices = CHOICES_MAP[choiceName];
  const target = choices.filter(choice => choice.value === value )[0];
  return target ? target.label : '';
};
// 選択肢名とパターンを渡すと、ラベルを返却
export const getChoicePatternLabel = (choiceName, pattern) => {
  if(!pattern) return '';

  const choices = CHOICES_MAP[choiceName];
  const target = choices.filter(choice => choice.pattern === pattern )[0];
  return target ? target.label : '';
};

// 選択肢名を渡すと、取りうる値を返却
export const getChoiceValues = (choiceName) => {
  const choices = CHOICES_MAP[choiceName];
  return choices.map(choice => choice.value);
};

// 時給の計算（端数）
export const calcSalary = salary => {
  return (Math.ceil(parseInt(salary, 10) / 10) * 10);
};

// 時給の計算(資格と経験における係数の取得)
export const calcSalaryCoefficient = (qualification, qualificationText, experience) => {
  const { experienceList, qualificationList } = CHOICES_MAP;
  let salaryFromExp = {};
  let salaryFromQua = {};

  if(qualification.length === 0){
    salaryFromQua = { max: qualificationText ? 80 : -110, min: -110 };
  }
  else {
    const salaryList = qualificationText ? [ 80 ] : [];
    qualificationList.forEach((qValue) => {
      if(qualification.includes(qValue.value)){
        salaryList.push(qValue.salary);
      }
    });
    salaryFromQua = { max: Math.max(...salaryList), min: Math.min(...salaryList) };
  }

  let salary = 0;
  if(!experience){
    salaryFromExp = { max: 0, min: 0 };
  } else {
    experienceList.forEach((eValue) => {
      if(experience === eValue.value){
        salary = eValue.salary;
      }
    });
    const experienceRequiredQualifications = qualificationList.filter((x) => x.experience ).map( (x) => x.value);
    salaryFromExp = {
      max: qualification.some(qValue => experienceRequiredQualifications.includes(qValue)) ? 50 : salary,
      min: salary,
    };
  }
  if((salaryFromExp.min + salaryFromQua.min) === (salaryFromExp.max + salaryFromQua.max)){
    return { min: salaryFromExp.min + salaryFromQua.min, max: salaryFromExp.max + salaryFromQua.max + 10 };
  } 
  return { min: salaryFromExp.min + salaryFromQua.min, max: salaryFromExp.max + salaryFromQua.max };
  
};

// 勤務時間の計算
export const calcBizTime = ({ startHour, startMin, endHour, endMin }) => {
  const start = parseInt(startHour, 10) * 60 + parseInt(startMin, 10);
  const end = parseInt(endHour, 10) * 60 + parseInt(endMin, 10);
  let result = (end - start) / 60;
  if(result < 0){
    result += 24;
  }
  return result;
};
// 入力チェックのスキーマ
const ajv = new Ajv({ allErrors: true });
// エラーオブジェクト
export class JbmErrorMessage {
  constructor(target, message, level = 9){
    this.target = target;
    this.message = message;
    this.level = level;
  }
}
// ドロップダウンの正規表現生成（完全一致）
const makeChoiceRegStr = (choiceName) => {
  // 初期値は空なので選択不可
  const okList = getChoiceValues(choiceName).filter(val => val !== DROPDOWN_INITIAL_ITEM.value);
  return `^(${okList.join('|')})$`;
};
// 先読み否定一致（1つでも含んでいたらNG）
const makeNotIncludeRegStr = (notIncludeList) => {
  return `^(?![\\s\\S]*(${notIncludeList.join('|')})).*`;
};
const PATTERN_MAP = {
  xss: makeNotIncludeRegStr([ '<', '>', '\'', '"',
    '①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩', '⑪', '⑫', '⑬', '⑭', '⑮', '⑯', '⑰', '⑱', '⑲', '⑳',
    'Ⅰ', 'Ⅱ', 'Ⅲ', 'Ⅳ', 'Ⅴ', 'Ⅵ', 'Ⅶ', 'Ⅷ', 'Ⅸ', 'Ⅹ',
    '㊤', '㊥', '㊦', '㊧', '㊨', '㍻', '㍼', '㍽', '㍾',
    '㎜', '㎝', '㎞', '℡', '№', '∮', '∑', '⊿', '∟', '㎡', '㈱', '㎎', '㏄', '㎏',
    '㌢', '㍉', '㌔', '㌧', '㍍', '㌃', '㌘', '㌶', '㌫', '㍑', '㍊', '㍗', '㌻', '㌍',
    '〝', '㌦', '〟', '㌣', '㏍', '㈲', '㈹',
    '纊', '褜', '鍈', '銈', '蓜', '俉', '炻', '昱', '棈', '鋹', '曻', '彅', '丨', '仡', '仼', '伀', '伃', '伹', '佖', '侒',
    '侊', '侚', '侔', '俍', '偀', '倢', '俿', '倞', '偆', '偰', '偂', '傔', '僴', '僘', '兊', '兤', '冝', '冾', '凬', '刕',
    '劜', '劦', '勀', '勛', '匀', '匇', '匤', '卲', '厓', '厲', '叝', '咜', '咊', '咩', '哿', '喆', '坙', '坥', '垬', '埈',
    '埇', '﨏', '塚', '增', '墲', '夋', '奓', '奛', '奝', '奣', '妤', '妺', '孖', '寀', '甯', '寘', '寬', '尞', '岦', '岺',
    '峵', '崧', '嵓', '﨑', '嵂', '嵭', '嶸', '嶹', '巐', '弡', '弴', '彧', '德', '忞', '恝', '悅', '悊', '惞', '惕', '愠',
    '惲', '愑', '愷', '愰', '憘', '戓', '抦', '揵', '摠', '撝', '擎', '敎', '昀', '昕', '昻', '昉', '昮', '昞', '昤', '晥',
    '晗', '晙', '晴', '晳', '暙', '暠', '暲', '暿', '曺', '朎', '朗', '杦', '枻', '桒', '柀', '栁', '桄', '棏', '﨓', '楨',
    '﨔', '榘', '槢', '樰', '橫', '橆', '橳', '橾', '櫢', '櫤', '毖', '氿', '汜', '沆', '汯', '泚', '洄', '涇', '浯', '涖',
    '涬', '淏', '淸', '淲', '淼', '渹', '湜', '渧', '渼', '溿', '澈', '澵', '濵', '瀅', '瀇', '瀨', '炅', '炫', '焏', '焄',
    '煜', '煆', '煇', '凞', '燁', '燾', '犱', '犾', '猤', '猪', '獷', '玽', '珉', '珖', '珣', '珒', '琇', '珵', '琦', '琪',
    '琩', '琮', '瑢', '璉', '璟', '甁', '畯', '皂', '皜', '皞', '皛', '皦', '益', '睆', '劯', '砡', '硎', '硤', '硺', '礰',
    '礼', '神', '祥', '禔', '福', '禛', '竑', '竧', '靖', '竫', '箞', '精', '絈', '絜', '綷', '綠', '緖', '繒', '罇', '羡',
    '羽', '茁', '荢', '荿', '菇', '菶', '葈', '蒴', '蕓', '蕙', '蕫', '﨟', '薰', '蘒', '﨡', '蠇', '裵', '訒', '訷', '詹',
    '誧', '誾', '諟', '諸', '諶', '譓', '譿', '賰', '賴', '贒', '赶', '﨣', '軏', '﨤', '逸', '遧', '郞', '都', '鄕', '鄧',
    '釚', '釗', '釞', '釭', '釮', '釤', '釥', '鈆', '鈐', '鈊', '鈺', '鉀', '鈼', '鉎', '鉙', '鉑', '鈹', '鉧', '銧', '鉷',
    '鉸', '鋧', '鋗', '鋙', '鋐', '﨧', '鋕', '鋠', '鋓', '錥', '錡', '鋻', '﨨', '錞', '鋿', '錝', '錂', '鍰', '鍗', '鎤',
    '鏆', '鏞', '鏸', '鐱', '鑅', '鑈', '閒', '隆', '﨩', '隝', '隯', '霳', '霻', '靃', '靍', '靏', '靑', '靕', '顗', '顥',
    '飯', '飼', '餧', '館', '馞', '驎', '髙', '髜', '魵', '魲', '鮏', '鮱', '鮻', '鰀', '鵰', '鵫', '鶴', '鸙', '黑'
  ]),
};
// ajv の入力チェックルール
// https://github.com/ajv-validator/ajv/blob/master/docs/json-schema.md
const schemas = {
  [STATION_URL]: {
    required: [ 'selectedEkiCd', 'moveTypeCd', 'moveTime', 'ekiName' ],
    properties: {
      ekiName: { type: 'string', minLength: 1, pattern: PATTERN_MAP.xss },
      selectedEkiCd: { type: 'string', pattern: '^[0-9]{5}$' },
      moveTypeCd: { type: 'string', pattern: makeChoiceRegStr('moveTypeCdList') },
      moveTime: { type: 'string', minLength: 1, pattern: '^[0-9]{1,3}$' },
      moveTimeFromBusStop: { type: 'string', minLength: 1, pattern: '^[0-9]{1,3}$' },
    },
    showErrorMessages: [ 'ekiName', 'moveTime', 'moveTimeFromBusStop' ],
  },
  [TYPE_URL]: {
    properties: {
      physicalCare: { 
        type: 'array', 
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('physicalCareList'),
        },
      },
      lifeSupport: { 
        type: 'array', 
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('lifeSupportList'),
        },
      },
      otherWorkText: { type: 'string', maxLength: 100, pattern: PATTERN_MAP.xss },
    },
    showErrorMessages: [ 'otherWorkText' ],
  },
  [SKILL_URL]: {
    required: [ 'suggestHourSalary' ],
    properties: {
      qualification: {
        type: 'array',
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('qualificationList'),
        },
      },
      qualificationText: { type: 'string', maxLength: 100, pattern: PATTERN_MAP.xss },
      experience: { type: 'string', pattern: makeChoiceRegStr('experienceList') },
      suggestHourSalary: { type: 'integer', maximum: 9999 },
    },
    showErrorMessages: [ 'qualificationText' ],
  },
  [COND_URL]: {
    required: [
      'fromDate',
      'toDate',
      'workStyle',
      'minBusinessDay',
      'maxBusinessDay',
      'dayOfTheWeek',
      'hourSalary',
      'canCommuteByCar',
    ],
    properties: {
      workStyle: { type: 'string', pattern: makeChoiceRegStr('workStyleList') },
      minBusinessDay: { type: 'string', pattern: makeChoiceRegStr('minBusinessDayList') },
      maxBusinessDay: { type: 'string', pattern: makeChoiceRegStr('maxBusinessDayList') },
      dayOfTheWeek: { type: 'array',
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('dayOfTheWeekList'),
        },
        minItems: 1,
        maxItems: 7, // 本来5
        uniqueItems: true,
      },
      workStyleKbn: { type: 'array', 
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('workStyleKbnList'),
        },
        minItems: 1,
        maxItems: 4,
        uniqueItems: true,
      },
      canCommuteByCar: { type: 'string', pattern: makeChoiceRegStr('canCommuteByCarList') },
      hourSalary: { type: 'string', minLength: 1, pattern: '^[0-9]{4}$' },
      freeComment: { type: 'string', maxLength: 200, pattern: PATTERN_MAP.xss },
      // 就業時間
      fixedStartHour: { type: 'string', pattern: makeChoiceRegStr('startHourList') },
      fixedStartMin: { type: 'string', pattern: makeChoiceRegStr('startMinList') },
      fixedEndHour: { type: 'string', pattern: makeChoiceRegStr('endHourList') },
      fixedEndMin: { type: 'string', pattern: makeChoiceRegStr('endMinList') },
      fixedRestTime: { type: 'string', pattern: makeChoiceRegStr('restTimeList') },

      earlyShiftStartHour: { type: 'string', pattern: makeChoiceRegStr('startHourList') },
      earlyShiftStartMin: { type: 'string', pattern: makeChoiceRegStr('startMinList') },
      earlyShiftEndHour: { type: 'string', pattern: makeChoiceRegStr('endHourList') },
      earlyShiftEndMin: { type: 'string', pattern: makeChoiceRegStr('endMinList') },
      earlyShiftRestTime: { type: 'string', pattern: makeChoiceRegStr('restTimeList') },

      dayShiftStartHour: { type: 'string', pattern: makeChoiceRegStr('startHourList') },
      dayShiftStartMin: { type: 'string', pattern: makeChoiceRegStr('startMinList') },
      dayShiftEndHour: { type: 'string', pattern: makeChoiceRegStr('endHourList') },
      dayShiftEndMin: { type: 'string', pattern: makeChoiceRegStr('endMinList') },
      dayShiftRestTime: { type: 'string', pattern: makeChoiceRegStr('restTimeList') },

      lateShiftStartHour: { type: 'string', pattern: makeChoiceRegStr('startHourList') },
      lateShiftStartMin: { type: 'string', pattern: makeChoiceRegStr('startMinList') },
      lateShiftEndHour: { type: 'string', pattern: makeChoiceRegStr('endHourList') },
      lateShiftEndMin: { type: 'string', pattern: makeChoiceRegStr('endMinList') },
      lateShiftRestTime: { type: 'string', pattern: makeChoiceRegStr('restTimeList') },
      
      nightShiftStartHour: { type: 'string', pattern: makeChoiceRegStr('startHourList') },
      nightShiftStartMin: { type: 'string', pattern: makeChoiceRegStr('startMinList') },
      nightShiftEndHour: { type: 'string', pattern: makeChoiceRegStr('endHourList') },
      nightShiftEndMin: { type: 'string', pattern: makeChoiceRegStr('endMinList') },
      nightShiftRestTime: { type: 'string', pattern: makeChoiceRegStr('restTimeList') },
    },
    showErrorMessages: [ 'syuugyouKikan', 'workStyle', 'dayOfTheWeek', 'canCommuteByCar', 'hourSalary', 'freeComment', 'workStyleKbn',
      'syuugyouTimeFixed', 'syuugyouTimeEarlyShift', 'syuugyouTimeDayShift', 'syuugyouTimeLateShift', 'syuugyouTimeNightShift',
      'restTimeFixed', 'restTimeEarlyShift', 'restTimeDayShift', 'restTimeLateShift', 'restTimeNightShift' ],
  },
  [INFO_URL]: {
    required: [ 'corpName', 'facilityName', 'facilityType', 'capacity', 'userName', 'tel', 'email', 'municipality', 'houseNumber' ],
    properties: {
      corpName: { type: 'string', maxLength: 300, minLength: 1, pattern: PATTERN_MAP.xss },
      facilityName: { type: 'string', maxLength: 300, minLength: 1, pattern: PATTERN_MAP.xss },
      facilityType: {
        type: 'array',
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('facilityTypeList'),
        },
        minItems: 1,
        uniqueItems: true,
      },
      capacity: { type: 'string', pattern: makeChoiceRegStr('capacityList') },
      userName: { type: 'string', maxLength: 150, minLength: 1, pattern: PATTERN_MAP.xss },
      tel: { type: 'string', pattern: '^([0-9-]{10,13})$', minLength: 1 },
      email: { type: 'string', pattern: '[\\w\\-._]+@[\\w\\-._]+\\.[A-Za-z]+', minLength: 1 },
      emailConf: { type: 'string', pattern: '[\\w\\-._]+@[\\w\\-._]+\\.[A-Za-z]+', minLength: 1 },
      zip: { type: 'string', if: { maxLength: 0 }, else: { pattern: '^([0-9]{3}-[0-9]{4}|[0-9]{7})$' } },
      municipality: { type: 'string', maxLength: 300, minLength: 1, pattern: PATTERN_MAP.xss },
      houseNumber: { type: 'string', maxLength: 300, minLength: 1, pattern: PATTERN_MAP.xss },
    },
    showErrorMessages: [ 'corpName', 'facilityName', 'facilityType', 'capacity', 'userName', 'tel', 'email', 'emailConf', 'zip', 'municipality', 'houseNumber' ],
  },
  [`${CONF_URL}:post`]: { // Post時に変換するパラメータのみ追加チェック 
    required: [
      'moveTime',
      'fromDate',
      'toDate',
      'minBusinessDay',
      'maxBusinessDay',
      'dayOfTheWeek',
      'workStyleInfo',
      'canCommuteByCar',
      'hourSalary',
      'suggestHourSalary',
      'address',
    ],
    properties: {
      moveTime: { type: 'integer', minimum: 0, maximum: 999 },
      fromDate: { type: 'string', pattern: '\\d{4}/\\d{2}/\\d{2}' },
      toDate: { type: 'string', pattern: '\\d{4}/\\d{2}/\\d{2}' },
      minBusinessDay: { type: 'integer', minimum: 1, maximum: 7 },
      maxBusinessDay: { type: 'integer', minimum: 1, maximum: 7 },
      dayOfTheWeek: {
        type: 'array',
        items: { type: 'integer' },
        maxItems: 7,
        uniqueItems: true,
      },
      workStyleInfo: {
        type: 'array',
        items: {
          workStyleKbn: { type: 'string', pattern: '01|02|03|04|05' },
          startTime: { type: 'string', pattern: '^(2[0-3]|[01][0-9]):([0-5][0-9])$' },
          endTime: { type: 'string', pattern: '^(2[0-3]|[01][0-9]):([0-5][0-9])$' },
          restTime: { type: 'integer', minimum: 0, maximum: 120 },
        },
        minItems: 1,
        maxItems: 5,
        uniqueItems: true,
      },
      canCommuteByCar: { type: 'boolean' },
      hourSalary: { type: 'integer', maximum: 9999 },
      address: { type: 'string', maxLength: 300, minLength: 1, pattern: PATTERN_MAP.xss },
    },
    showErrorMessages: [],
  },
};

// ajv 以外の相関チェック、APIを利用したチェックなど
const customChecks = {
  // 駅がサービス対象外
  checkEkiCode: async ({ ekiCd }) => {
    const target = 'ekiCd';
    const ok = null ;
    const ng = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.checkEkiCodeNG, 1);

    if( !ekiCd || ekiCd === '') return ok;

    const valid = await checkEkiCodeApi({ ekiCd })
      .catch(err => {
        console.log('checkEkiCode: catch');
        if(err.response.status === 400) return false;
        throw err;
      });
    return valid ? ok : ng;
  },

  // 業務内容のいずれか1つにチェックがあるか
  checkOrderType: async ({ physicalCare, lifeSupport, otherWorkText }) => {
    const target = 'orderType';
    const ok = null;
    const ng = new JbmErrorMessage(target, null, 0);
    const other = otherWorkText.trim();

    if ( physicalCare && physicalCare.length > 0 ) {
      return ok;
    }
    if ( lifeSupport && lifeSupport.length > 0 ) {
      return ok;
    }
    if ( other ) {
      return ok;
    }
    return ng;
  },

  // 介護資格いずれか必須チェック
  checkAnyQualifications: async({ qualification, qualificationText }) => {
    const target = 'qualificationText';
    const ok = null;
    const ng = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.qualificationsNG, 1);
    if ( qualification && qualification.length > 0 ) {
      console.log('qualification OK');
      return ok;
    }
    if ( qualificationText && qualificationText.length > 0 ) {
      console.log('qualificationText OK');
      return ok;
    }
    console.log('NG');
    return ng;
  },

  // Cond: 期間チェック
  checkSyuugyouKikan: async ({ fromDate, toDate, pendingFromDateFlag, pendingToDateFlag }) => {
    const target = 'syuugyouKikan';
    const ok = null ;
    const syuugyouKikanNG = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.syuugyouKikanNG, 1);
    const startNg = new JbmErrorMessage(target, null, 0);
    const syuugyouKikan31NG = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.syuugyouKikan31NG, 1);
    if((!fromDate && !pendingFromDateFlag) 
      || !toDate && !pendingToDateFlag) {
      return startNg;
    }
    if(pendingFromDateFlag && !!toDate) {
      return ok;
    } if (pendingToDateFlag && !!fromDate){
      return ok;
    }
    // 開始日＜終了日の場合OK
    const minDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate() + 31);
    if (toDate < minDate) {
      return syuugyouKikan31NG;
    }
    const valid = fromDate <= toDate; // typeOf(fromDate) === 'date' && typeOf(toDate) === 'date'
    return valid ? ok : syuugyouKikanNG;
  },

  // Cond: 就業日数 > 就業曜日
  checkDayOfTheWeek: async ({ minBusinessDay, dayOfTheWeek }) => {
    const target = 'dayOfTheWeek';
    const ok = null ;
    const ng = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.dayOfTheWeekNG, 1);

    // いずれか未入力はOK
    if(!minBusinessDay || dayOfTheWeek.length === 0) return ok;

    const valid = dayOfTheWeek.length >= parseInt(minBusinessDay, 10);
    return valid ? ok : ng;
  },

  // Cond: 終了時刻 - 開始時刻 - 休憩時間 > 8
  checkOver8Hours: async ({ target, startHour, startMin, endHour, endMin, restTime }) => {
    const ok = null ;
    const ng = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.over8HoursNG, 1);

    // いずれか未入力はOK
    if(!startHour || !startMin || !endHour || !endMin || !restTime) return ok;

    console.log({ biz: calcBizTime({ startHour, startMin, endHour, endMin }), rest: (parseFloat(restTime) / 60) });
    const valid = (calcBizTime({ startHour, startMin, endHour, endMin }) - (parseFloat(restTime) / 60)) <= 8;
    if(!valid) return ng;
    return ok;
  },
  
  // Cond: 終了時刻 - 開始時刻 - 休憩時間 <= 0
  checkLessThan0Hour: async ({ target, startHour, startMin, endHour, endMin, restTime }) => {
    const ok = null ;
    const ng = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.syuugyouTimeNG, 1);

    // いずれか未入力はOK
    if(!startHour || !startMin || !endHour || !endMin || !restTime) return ok;

    console.log({ biz: calcBizTime({ startHour, startMin, endHour, endMin }), rest: (parseFloat(restTime) / 60) });
    const valid = (calcBizTime({ startHour, startMin, endHour, endMin }) - (parseFloat(restTime) / 60)) > 0;
    if(!valid) return ng;
    return ok;
  },

  // Cond: 日雇い 
  checkWeek20hour: async ({ target, startHour, startMin, endHour, endMin, restTime, minBusinessDay }) => {
    const ok = null ;
    const ng = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.week20hourNG, 1);

    // いずれか未入力はOK
    if(!startHour || !startMin || !endHour || !endMin || !restTime || !minBusinessDay) return ok;
    const valid = (calcBizTime({ startHour, startMin, endHour, endMin }) - (parseFloat(restTime) / 60)) * minBusinessDay >= 20;
    if(!valid) return ng;
    
    return ok;
  },

  // Cond: 終了時刻 - 開始時刻 - 休憩時間 > 6 かつ 休憩時間 < 45
  checkRestTime: async ({ target, startHour, startMin, endHour, endMin, restTime, isSp }) => {
    const ok = null ;
    const pcRestTimeNG = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.restTimeNG, 2);
    const spRestTimeNG = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.restTimeNG, 1);

    // いずれか未入力はOK
    if(!startHour || !startMin || !endHour || !endMin || !restTime) return ok;

    console.log({ biz: calcBizTime({ startHour, startMin, endHour, endMin }), rest: parseInt(restTime, 10) });
  
    if ((calcBizTime({ startHour, startMin, endHour, endMin }) - (parseFloat(restTime) / 60)) > 6 ) {
      if(parseInt(restTime, 10) >= 45){
        return ok;
      }
      return isSp ? spRestTimeNG : pcRestTimeNG;
    } 
    return ok;
  },
  
  checkBusinessDay: async ({ minBusinessDay, maxBusinessDay }) => {
    const target = 'businessDay';
    const ok = null ;
    const ng = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.businessDayNG, 1);

    // いずれか未入力はOK
    if(!minBusinessDay || !maxBusinessDay) return ok;
    return minBusinessDay > maxBusinessDay ? ng : ok;
  },

  // メールアドレス、メールアドレス(確認)が完全一致しているか
  checkEmailMatch: async ({ email, emailConf }) => {
    const target = 'emailConf';
    const ok = null ;
    const ng = new JbmErrorMessage(target, CUSTOM_ERROR_MESSAGES.emailConfNG, 1);

    // いずれか未入力はOK
    if(!email || !emailConf) return ok;
    return email === emailConf ? ok : ng ;
  },
};

// エラーをレベルでソート(0: 最優先)
export const sortErrors = (errors) => {
  errors.sort((a, b) => {
    if ( a.level === b.level ) { return 0; }
    return a.level < b.level ? -1 : 1;
  });
  return errors;
};

// 各画面でのvalidation
// カスタムチェック
export const isCustomValid = async ({ checks: checkFuncNames, target }) => {

  // カスタムエラーチェック
  const errorResult = await Promise.all(
    checkFuncNames.map(async (name) => {
      const ret = await customChecks[name]({ ...target })
        .catch(err => {
          console.log('customChecks');
          throw err;
        });

      return ret ? {
        ...ret,
        custom: true,
      } : null;
    }),
  ).catch(err => {
    console.log('Promise.all');
    throw err;
  });
  const customErrorMessages = errorResult.filter(x => !!x);
  const customErrorValid = customErrorMessages.length === 0;
  
  // エラーを返却
  return [
    customErrorValid,
    customErrorMessages,
  ];
};
// schema による静的チェック
export const isSchemaValid = ({ path = window.location.pathname, target }) => {

  // チェックルールを取得
  const schema = schemas[path];
  if(!schema) return [ true, [] ];

  // ajvによるチェック
  const validate = ajv.compile(schema);
  const valid = validate(target);
  // ajvによるエラー
  const errors = validate.errors ? validate.errors : [];
  const errorMessages = errors
    .map(error => {
      const dataPath = error.dataPath.replace(/^\./, '');
      const ok = null;
      // エラーメッセージ表示対象のみ表示
      if(!schema.showErrorMessages.includes(dataPath)) {
        return ok;
      }

      // エラーメッセージが存在するエラーのみ表示
      const message = ERROR_MESSAGES.filter(e => {
        if(error.keyword === "pattern" && error.params.pattern === PATTERN_MAP.xss) {
          error.message = error.message.replace("should match pattern", "xss error");
        }
        return !!error.message.match(e.pattern);
      })[0];
      if(message.length === 0) {
        return ok;
      }

      // エラーメッセージを返却
      if(NO_NAME_ERROR_MESSAGES.includes(message.label)) {
        return new JbmErrorMessage(dataPath, `${message.label}`, 0);
      } else {
        const name = CHOICES_NAME_MAP[dataPath];
        return new JbmErrorMessage(dataPath, `${name}${message.label}`, 0);
      }
    }).filter(x => !!x);

  console.log({ errorMessages });
  
  // NGの場合のコンソール表示
  if(!valid){
    console.log(
      '%c[DEFAULT VALIDATE_ERROR]',
      'color:blue;',
    );
    console.log({
      path,
      target,
      schema,
      valid,
      validate,
      erros: errorMessages,
    });
  }

  // エラーを返却
  return [
    valid,
    errorMessages,
  ];
};

// Web計測を実施
export const useAnalytics = (lpMode) => {
  const isAnalytics = env.IS_ANALYTICS;
  const location = useLocation();
  let lpStr = '';
  if(lpMode){
    lpStr = ':include';
  }

  useEffect(()=>{
    if( !isAnalytics ) return;
    
    switch(location.pathname){
      case STATION_URL:
        Measure({
          pageName: `sol:jobma:order:station${lpStr}:index.html`,
          title: '【Jobマ!】駅選択画面',
        }, false);
        break;
      case TYPE_URL:
        Measure({
          pageName: `sol:jobma:order:type${lpStr}:index.html`,
          title: '【Jobマ!】業務内容選択画面',
        }, false);
        break;
      case SKILL_URL:
        Measure({
          pageName: `sol:jobma:order:skill${lpStr}:index.html`,
          title: '【Jobマ!】スキル選択画面',
        }, false);
        break;
      case COND_URL:
        Measure({
          pageName: `sol:jobma:order:cond${lpStr}:index.html`,
          title: '【Jobマ!】条件選択画面',
        }, false);
        break;
      case INFO_URL:
        Measure({
          pageName: `sol:jobma:order:info${lpStr}:index.html`,
          title: '【Jobマ!】担当者情報入力画面',
        }, false);
        break;
      case CONF_URL:
        Measure({
          pageName: `sol:jobma:order:conf${lpStr}:index.html`,
          title: '【Jobマ!】確認画面',
        }, false);
        break;
      case THANKS_URL:
        Measure({
          pageName: `sol:jobma:order:thanks${lpStr}:index.html`,
          title: '【Jobマ!】完了画面',
        }, false);
        break;
      default:
        break;
    }
  }, [ isAnalytics, location.pathname, lpStr ] );
};

// 駅選択画面遷移時にGetパラメータのチェックを実施
export const useStationParamCheck = ({ state }) => {
  // console.log({ env, state });
  const history = useHistory();
  const location = useLocation();

  const { dispatch } = useContext(AppContext);
  const params = queryString.parse( location.search );
  const { link, ekiName, ekiCd, moveTypeCd, moveTime } = params;
  const path = STATION_URL;
  
  const isExistsGetParam = () => {
    const targetList = [ ekiName, ekiCd, moveTypeCd, moveTime ];
    const ret = targetList.every(x => x !== undefined);
    return ret;
  };
  const isExistsState = () => {
    const targetList = [
      state.ekiName,
      state.ekiCd,
      state.moveTypeCd,
      state.moveTime,
    ];
    // console.log({ targetList, ret: targetList.every(x => x !== undefined && x !== '') });
    const ret = targetList.every(x => x !== undefined && x !== ''); 
    return ret;
  };

  useEffect(() => {

    if(isExistsState()) return;

    // LPから来た場合（駅未入力）→駅入力ページへ
    if(link === 'lp'){
      console.log('lpkarakitayo');
    
    // LPから来た場合（駅入力済）→仕事内容選択ページへ（type）
    }else if(isExistsGetParam()){
      const updStation = { 
        ekiName,
        ekiCd,
        moveTypeCd,
        moveTime,
      };
      
      // state を更新する
      dispatch({
        type: UPDATE_STATION, 
        updStation,
      });

      // 入力チェックOKなら、次の画面へ
      const valid = isSchemaValid({ path, target: updStation });
      if(valid){
        dispatch({
          type: CHANGE_VALID, 
          path,
          valid,
        });
        history.push(TYPE_URL);
        console.log('aru');
      }

    // 直接駅入力ページ来た場合、LPにリダイレクト
    } else {
      console.log('[REDIRECT] useStationParamCheck');
      const redirectUrl = env.ENV === 'local' ? `${STATION_URL}?link=lp` : LP_URL;
      window.location.href = redirectUrl;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// 直接遷移を確認
export const useDirectTransCheck = ({ state }) => {
  const path = window.location.pathname;
  const { prevUrl } = PAGE_MAP[path];
  const { validMap } = state;

  const redirectUrl = env.ENV === 'local' ? STATION_URL : LP_URL;

  useEffect(()=> {
    if(prevUrl && !validMap[prevUrl]){
      console.log('[REDIRECT] useDirectTransCheck');
      // schemas[prevUrl].required.forEach(prop => console.log(`${prop}=> ${state[prop]}`));
      window.location.href = redirectUrl;
    } 
  }, [ prevUrl, redirectUrl, validMap ]);
};
 
// window 幅、高さを取得
export const useWindowDimensions = () => {
 
  const getWindowDimensions = () => {
    const {
      innerHeight: height,
    } = window;
    const {
      offsetWidth: width,
    } = window.document.body;

    return {
      width,
      height,
    };
  };
 
  const [ windowDimensions, setWindowDimensions ] = useState(getWindowDimensions());
  useEffect(() => {
    const onResize = () => {
      setWindowDimensions(getWindowDimensions());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return windowDimensions;
};
// UAがIEかどうか判定
export const useIECheck = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIE = userAgent.indexOf('msie') !== -1 || userAgent.indexOf('trident') !== -1;
  return isIE;
};

// 画面遷移確認ダイアログ
export const MoveCheckMsg = (e) => {
  e.preventDefault();
  e.returnValue = MOVE_CONF_MSG;
};
export const MoveCheckStart = () => {
  window.addEventListener('beforeunload', MoveCheckMsg);
  console.log('addEventListener beforeunload');
};
export const MoveCheckStop = () => {
  window.removeEventListener('beforeunload', MoveCheckMsg);
  console.log('removeEventListener beforeunload');
};

export const postHeightToParent = (origin, contHeight) => {
  const stage = process.env.REACT_APP_STAGE || 'local';
  const path = window.location.pathname;
  let postPrigin = '*';
  if(stage === 'master' || stage === 'staging'){
    postPrigin = origin;
  }
  window.parent.postMessage({ action: 'adjustHeight', height: contHeight }, postPrigin);
  if(path !== STATION_URL){
    window.parent.postMessage({ action: 'scrollTo', scrollTarget: '.p-form' }, postPrigin);
  }
};
